import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export interface MembershipDiscount {
  code: string;
  discount_percentage: number;
  applies_to_tags: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  partner_name: string;
  max_uses_per_month: number | null;
  /** How many matching-tag tickets the user already has this month */
  monthly_uses: number;
}

export function getBestMembershipDiscount(
  discounts: MembershipDiscount[],
  eventTags: string[]
): { discount: MembershipDiscount; label: string } | null {
  if (!discounts.length || !eventTags.length) return null;

  const normalizedTags = eventTags.map((t) => t.toLowerCase());
  let best: MembershipDiscount | null = null;

  for (const d of discounts) {
    // Check date validity
    if (d.valid_until && new Date(d.valid_until) < new Date()) continue;
    if (d.valid_from && new Date(d.valid_from) > new Date()) continue;

    // Check tag match
    const tagMatch =
      !d.applies_to_tags ||
      d.applies_to_tags.includes("all") ||
      d.applies_to_tags.some((tag) => normalizedTags.includes(tag.toLowerCase()));

    if (!tagMatch) continue;

    // Check monthly usage limit
    if (d.max_uses_per_month !== null && d.monthly_uses >= d.max_uses_per_month) continue;

    if (!best || d.discount_percentage > best.discount_percentage) {
      best = d;
    }
  }

  if (!best) return null;

  const label = "Friends Perk";

  return { discount: best, label };
}

export const useMembershipDiscountStatus = () => {
  const { userId } = useAuth();

  return useQuery({
    queryKey: ["membership-discount-status", userId],
    queryFn: async (): Promise<MembershipDiscount[]> => {
      if (!userId) return [];

      // Step 1: Get user's active subscription tier_slug
      const { data: subscription, error: subError } = await supabase
        .from("user_subscriptions")
        .select("package_id")
        .eq("user_id", userId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subError || !subscription) return [];

      // Step 2: Get tier_slug from the campaign package
      const { data: pkg, error: pkgError } = await supabase
        .from("campaign_packages")
        .select("tier_slug")
        .eq("id", subscription.package_id)
        .maybeSingle();

      if (pkgError || !pkg?.tier_slug) return [];

      const tierSlug = pkg.tier_slug;

      // Step 3: Get all active percentage partner codes for this tier
      const { data: codes, error: codesError } = await supabase
        .from("partner_codes")
        .select("code, discount_percentage, applies_to_tags, valid_from, valid_until, partner_name, max_uses_per_month")
        .eq("tier_slug", tierSlug)
        .eq("is_active", true)
        .eq("discount_type", "percentage");

      if (codesError || !codes?.length) return [];

      // Step 4: For codes with max_uses_per_month, count this month's confirmed tickets
      // with matching tags
      const codesWithLimits = codes.filter((c: any) => c.max_uses_per_month !== null);

      let monthlyUsesMap: Record<string, number> = {};

      if (codesWithLimits.length > 0) {
        // Get start of current month in UTC
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        // Collect all tags we need to check
        const allLimitTags = new Set<string>();
        for (const c of codesWithLimits) {
          if (c.applies_to_tags) {
            for (const tag of c.applies_to_tags as string[]) {
              allLimitTags.add(tag.toLowerCase());
            }
          }
        }

        if (allLimitTags.size > 0) {
          // Get user's confirmed tickets this month with their event tags
          const { data: tickets } = await supabase
            .from("tickets")
            .select("event_id, events!inner(start_time), quantity")
            .eq("user_id", userId)
            .eq("status", "confirmed")
            .gte("created_at", monthStart);

          if (tickets?.length) {
            const eventIds = tickets.map((t: any) => t.event_id).filter(Boolean);

            // Get tags for these events
            const { data: eventTags } = await supabase
              .from("event_tags")
              .select("event_id, tag")
              .in("event_id", eventIds);

            // Build map of event_id -> tags
            const eventTagMap: Record<string, string[]> = {};
            for (const et of eventTags || []) {
              if (!eventTagMap[et.event_id!]) eventTagMap[et.event_id!] = [];
              eventTagMap[et.event_id!].push(et.tag.toLowerCase());
            }

            // For each code with limits, count matching tickets
            for (const c of codesWithLimits) {
              const codeTags = (c.applies_to_tags as string[] || []).map((t: string) => t.toLowerCase());
              let count = 0;
              for (const ticket of tickets) {
                const tags = eventTagMap[ticket.event_id] || [];
                if (codeTags.some((ct: string) => tags.includes(ct))) {
                  count += (ticket as any).quantity || 1;
                }
              }
              monthlyUsesMap[c.code] = count;
            }
          }
        }
      }

      return codes.map((c: any) => ({
        code: c.code,
        discount_percentage: c.discount_percentage || 0,
        applies_to_tags: c.applies_to_tags,
        valid_from: c.valid_from,
        valid_until: c.valid_until,
        partner_name: c.partner_name,
        max_uses_per_month: c.max_uses_per_month,
        monthly_uses: monthlyUsesMap[c.code] || 0,
      }));
    },
    enabled: !!userId,
  });
};
